import {TaskDropdown} from "@/shared/task/taskDropdown";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {formatTime, Temporal} from "@/shared/utils/datefunctions";
import {Task} from "@/shared/timr";
import {EVENT_RECOMMENDED_TASK_SELECTED, loadAndDisplayRecommendedTasks} from "@/shared/task/taskRecommendationHelper";
export interface SwitchTaskDialogConfig {
	container: JQuery;
}

export class SwitchTaskDialog {

	public static readonly EVENT_CANCELED = "canceled";

	private dialog: BootstrapModal.Modal;
	private taskDropdown: TaskDropdown;
	private endTime: Temporal.ZonedDateTime | null;
	private taskSwitchExecutor: (taskId: number) => JQueryPromise<unknown>;

	constructor(public config: SwitchTaskDialogConfig) {
		this.init();
	}

	public show(endTime: Temporal.ZonedDateTime | null, taskSwitchExecutor: (taskId: number) => JQueryPromise<unknown>) {
		this.endTime = endTime;
		this.taskSwitchExecutor = taskSwitchExecutor;

		// the refresh by the library is a bit slow, so you can see the previous active node on opening
		this.config.container.find(".fancytree-node").removeClass("fancytree-active");
		this.taskDropdown.queryIfNeeded();

		this.dialog.setTitle(messages.withArguments("switchTaskDialog.title", formatTime(this.endTime)));
		this.dialog.show();
	}

	public isVisible(): boolean {
		return this.config.container.is(":visible");
	}

	public connect(otherTaskDropdown: TaskDropdown) {
		TaskDropdown.connect(this.taskDropdown, otherTaskDropdown);
	}

	private init() {
		this.dialog = new BootstrapModal.Modal({
			title: "switchTaskDialog.title",
			message: this.config.container,
			size: "modal-lg",
			destroyOnClose: false,
			buttons: [{
				text: "dialog.cancel",
				css: "cancelButton btn-default"
			}],
			closeOnOutsideClick: true,
			onClose: (_, reason: string) => {
				// Only remove selected listener to not remove the connection listener
				$(this.taskDropdown).off(TaskDropdown.EVENT_SELECTED);

				if (reason === BootstrapModal.CLOSE_REASON_INTERNAL) {
					$(this).triggerHandler(SwitchTaskDialog.EVENT_CANCELED);
				}

				this.taskDropdown.closeDropdown();
				Button.resetLoading(this.dialog.getButtons("cancelButton"));
			},
			onOpen: () => {
				this.bindTaskDropdown();
				this.bindRecommendedTasks();
				this.config.container.find($("[data-task-query]"))
					.focus()
					.on("keydown", (e) => {
						// Close dialog on ESC
						if (e.keyCode === 27) {
							this.dialog.close(BootstrapModal.CLOSE_REASON_INTERNAL);
							return false;
						}
					});
			}
		});

		$(this.dialog).on("show.bs.modal", () => {
			void loadAndDisplayRecommendedTasks(
				$(this.dialog),
				this.config.container.find("[data-recommended-tasks]"),
				this.config.container.find("[data-recent-tasks]"),
				this.endTime
			);
		});

		this.taskDropdown = new TaskDropdown({
			rootElement: $("#projectTimeSwitchTaskDropdown"),
			allowSelectionOfNotBookable: false,
			hideInactive: true,
			emptyMessage: ""
		}, {
			loadRootNodesURL: "recording/loadAllTasks.json",
			loadSubNodesURL: "recording/treeLoad.json",
			filterNodesURL: "recording/filterTasks.json"
		});
	}

	private bindTaskDropdown() {
		this.setTaskDropdownLoadingState(false);
		$(this.taskDropdown).off(TaskDropdown.EVENT_SELECTED);
		$(this.taskDropdown).on(TaskDropdown.EVENT_SELECTED, (e, task: Task) => {
			// Only remove selected listener to not remove the connection listener
			$(this.taskDropdown).off(TaskDropdown.EVENT_SELECTED);

			this.setTaskDropdownLoadingState(true);
			this.switchTask(task.id);
		});
	}

	private bindRecommendedTasks() {
		this.setTaskDropdownLoadingState(false);
		$(this.dialog).off(EVENT_RECOMMENDED_TASK_SELECTED);
		$(this.dialog).on(EVENT_RECOMMENDED_TASK_SELECTED, (e, task: Task) => {
			this.setTaskDropdownLoadingState(true);
			this.switchTask(task.id);
		});
	}

	private setTaskDropdownLoadingState(setLoading: boolean) {
		const container = this.config.container.find("ul.fancytree-container");
		const inputFields = this.config.container.find("input");
		const tasksSection = this.config.container.find("[data-tasks-section]");
		const cancelButton = this.dialog.getButtons("cancelButton");

		inputFields.prop("disabled", setLoading);
		container.toggleClass("ui-fancytree-disabled", setLoading);
		tasksSection.toggleClass("ui-tasks-disabled", setLoading);
		if (setLoading) {
			Button.setLoading(cancelButton);
		} else {
			Button.resetLoading(cancelButton);
		}
	}

	private switchTask(taskId: number) {
		if (taskId) {
			this.taskSwitchExecutor(taskId)
				.then(() => {
					this.dialog.close();
				}, () => {
					// Rebinding restores the task selection handler (it removes itself) and resets the loading state
					this.bindTaskDropdown();
				});
		}
	}
}
